import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Mic,
  Mail,
  UserCircle2,
  Clock,
  CheckCircle2,
  Send,
  Inbox,
} from 'lucide-react';
import axios from 'axios';

import { useGlobalContext } from '../AUTH/GlobalContext.js';
import ProfileSetupForm from './ProfileSetupForm.jsx';

const formatDate = () => {
  const today = new Date();
  return today.toDateString();
};

const timeAgo = (dateString) => {
  if (!dateString) return '';
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ACTIVITY_LABEL = {
  thread_start: 'Email sent to',
  follow_up: 'Follow-up sent to',
  closed: 'Conversation closed with',
};

// How complete the user's profile is — same fields profile.js renders.
const computeProfileCompletion = (profileData) => {
  if (!profileData) return 0;

  const hasRoleSpecificInfo =
    profileData.userType === 'working_professional'
      ? !!profileData?.professionalDetails?.jobTitle
      : profileData.userType === 'student'
      ? !!profileData?.studentDetails?.college
      : false;

  const checks = [
    !!profileData.phone,
    !!profileData.bio,
    !!profileData?.details?.location,
    Array.isArray(profileData?.details?.skills) && profileData.details.skills.length > 0,
    hasRoleSpecificInfo,
    (Array.isArray(profileData.experience) && profileData.experience.length > 0) ||
      (Array.isArray(profileData.projects) && profileData.projects.length > 0),
  ];

  const passed = checks.filter(Boolean).length;
  return Math.round((passed / checks.length) * 100);
};

const Dashboard = () => {
  const { user, userProfile, setUser } = useGlobalContext();

  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Check if user needs to complete profile setup
  useEffect(() => {
    if (user && (!user.userType || user.userType === "None")) {
      setShowProfileSetup(true);
    } else {
      setShowProfileSetup(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;

    const token = localStorage.getItem('jwtToken');
    setLoadingStats(true);

    Promise.allSettled([
      axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/logs/all`, {
        params: { userEmail: user.email },
        headers: { Authorization: `Bearer ${token}` },
      }),
      user._id
        ? axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/interviews/history`, {
            params: { userId: user._id },
          })
        : Promise.resolve(null),
    ]).then(([logsResult, interviewsResult]) => {
      if (logsResult.status === 'fulfilled' && logsResult.value?.data?.success) {
        setEmailLogs(logsResult.value.data.logs || []);
      }
      if (interviewsResult.status === 'fulfilled' && interviewsResult.value?.data?.success) {
        setInterviews(interviewsResult.value.data.interviews || []);
      }
      setLoadingStats(false);
    });
  }, [user]);

  // Handle profile setup completion
  const handleProfileSetupComplete = async (data) => {
    if (data.skipped) {
      setShowProfileSetup(false);
      return;
    }

    if (user) {
      setUser({
        ...user,
        userType: data.userType || user.userType
      });
    }

    setShowProfileSetup(false);
  };

  // Show ProfileSetupForm if userType is "None"
  if (showProfileSetup && user?._id) {
    return (
      <ProfileSetupForm
        userId={user._id}
        onComplete={handleProfileSetupComplete}
      />
    );
  }

  const profileData = userProfile || user;
  const profileCompletion = computeProfileCompletion(profileData);

  const threadStarts = emailLogs.filter((l) => l.status === 'thread_start');
  const followUps = emailLogs.filter((l) => l.status === 'follow_up');
  const closed = emailLogs.filter((l) => l.status === 'closed');
  const recruitersContacted = new Set(threadStarts.map((l) => l.recruiterId)).size;

  const funnel = [
    { label: 'Emails Sent', count: threadStarts.length, color: 'from-blue-500 to-indigo-500' },
    { label: 'Follow-ups Sent', count: followUps.length, color: 'from-amber-400 to-orange-500' },
    { label: 'Closed', count: closed.length, color: 'from-gray-400 to-gray-500' },
  ];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));

  const recentActivity = [...emailLogs]
    .filter((l) => l.status !== 'no-reply')
    .sort((a, b) => new Date(b.createdAt || b.sentAt) - new Date(a.createdAt || a.sentAt))
    .slice(0, 6);

  const statCards = [
    { label: 'Recruiters Contacted', value: recruitersContacted, icon: Users, bg: 'bg-blue-50', color: 'text-blue-600' },
    { label: 'Interviews Completed', value: interviews.length, icon: Mic, bg: 'bg-purple-50', color: 'text-purple-600' },
    { label: 'Emails Sent', value: emailLogs.length, icon: Mail, bg: 'bg-amber-50', color: 'text-amber-600' },
    { label: 'Profile Completion', value: `${profileCompletion}%`, icon: UserCircle2, bg: 'bg-green-50', color: 'text-green-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Welcome, {user?.username || user?.name || "User"}
          </h1>
          <p className="text-gray-500 mb-8">{formatDate()}</p>
        </motion.div>

        {/* STAT CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                whileHover={{ y: -3 }}
                className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <span className={`flex items-center justify-center w-12 h-12 rounded-xl ${stat.bg} shrink-0`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500 truncate">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {loadingStats ? (
                        <span className="inline-block w-10 h-6 bg-gray-100 rounded animate-pulse" />
                      ) : (
                        stat.value
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* OUTREACH FUNNEL */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="text-base font-semibold text-gray-900 mb-5">Outreach Activity</h2>

            {emailLogs.length === 0 && !loadingStats ? (
              <div className="text-center py-10">
                <Send className="w-9 h-9 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No outreach yet — send your first email to see activity here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {funnel.map((row, index) => (
                  <div key={row.label}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-gray-600">{row.label}</span>
                      <span className="font-semibold text-gray-900">{row.count}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(row.count / funnelMax) * 100}%` }}
                        transition={{ duration: 0.7, delay: 0.2 + index * 0.1, ease: 'easeOut' }}
                        className={`h-full rounded-full bg-gradient-to-r ${row.color}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* RECENT ACTIVITY */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="text-base font-semibold text-gray-900 mb-5">Recent Activity</h2>

            {recentActivity.length === 0 && !loadingStats ? (
              <div className="text-center py-10">
                <Inbox className="w-9 h-9 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Nothing here yet.</p>
              </div>
            ) : (
              <ul className="space-y-4">
                {recentActivity.map((log, index) => (
                  <motion.li
                    key={log._id || index}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 shrink-0 mt-0.5">
                      {log.status === 'closed' ? (
                        <CheckCircle2 className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Mail className="w-4 h-4 text-blue-600" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">
                        {ACTIVITY_LABEL[log.status] || 'Activity with'}{' '}
                        <span className="font-medium">{log.sentTo}</span>
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {timeAgo(log.createdAt || log.sentAt)}
                      </p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
